import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Logout from "../assets/icons/logout.png";
import TopItems from "../pages/TopItems";
import { baseUrl } from "../utils/constants";
import {
  findActiveNavigationGroup,
  getAdminNavigation,
  isNavigationEntryActive
} from "../navigation/adminNavigation";
import CollapsedNavigationFlyout from "./navigation/CollapsedNavigationFlyout";
import useMobileDrawerInteractions from "./navigation/useMobileDrawerInteractions";
import {
  clearAdminToken,
  getAdminToken
} from "../auth/adminSession";
import {
  prefetchDefaultRegistrationReviewQueue
} from "../features/userManagement/registrationReviewQueueCache";
import "../css/navbar.css";

const navigationText = {
  en: {
    logout: "Log Out",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    adminNavigation: "Admin navigation"
  },
  ar: {
    logout: "تسجيل الخروج",
    expand: "توسيع القائمة",
    collapse: "طي القائمة",
    adminNavigation: "قائمة الإدارة"
  }
};

const Navbar = ({
  showLanguageToggle = false,
  language = "en",
  onToggleLanguage,
  expanded = true,
  onToggleExpanded = () => {},
  isMobile = false,
  mobileMenuOpen = false,
  onOpenMobileMenu = () => {},
  onCloseMobileMenu = () => {},
  reloadPage = () => window.location.reload()
}) => {
  const location = useLocation();
  const selectedLanguage = language === "ar" ? "ar" : "en";
  const direction = selectedLanguage === "ar" ? "rtl" : "ltr";
  const labels = navigationText[selectedLanguage];
  const navigation = useMemo(
    () => getAdminNavigation(selectedLanguage),
    [selectedLanguage]
  );
  const activeGroupKey = useMemo(
    () => findActiveNavigationGroup(navigation, location.pathname),
    [location.pathname, navigation]
  );
  const [openInlineGroupKey, setOpenInlineGroupKey] = useState(activeGroupKey);
  const [openFlyoutGroupKey, setOpenFlyoutGroupKey] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({});
  const groupTriggerRefs = useRef({});
  const flyoutRef = useRef(null);
  const mobileDrawerRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const previousPathRef = useRef(location.pathname);
  const token = getAdminToken();

  const activeFlyoutGroup = navigation.find(
    (entry) => entry.type === "group" && entry.key === openFlyoutGroupKey
  );

  useEffect(() => {
    setOpenFlyoutGroupKey(null);
    setOpenInlineGroupKey((expanded || isMobile) ? activeGroupKey : null);
  }, [activeGroupKey, expanded, isMobile, location.pathname, selectedLanguage]);

  const closeMobileMenu = useCallback(() => {
    onCloseMobileMenu();
    mobileToggleRef.current?.focus();
  }, [onCloseMobileMenu]);

  const openMobileMenu = useCallback(() => {
    setOpenFlyoutGroupKey(null);
    onOpenMobileMenu();
  }, [onOpenMobileMenu]);

  useMobileDrawerInteractions({
    active: isMobile && mobileMenuOpen,
    drawerRef: mobileDrawerRef,
    toggleRef: mobileToggleRef,
    onClose: closeMobileMenu
  });

  useEffect(() => {
    if (previousPathRef.current !== location.pathname && mobileMenuOpen) {
      closeMobileMenu();
    }
    previousPathRef.current = location.pathname;
  }, [closeMobileMenu, location.pathname, mobileMenuOpen]);

  const updateFlyoutPosition = useCallback(() => {
    const trigger = groupTriggerRefs.current[openFlyoutGroupKey];
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const sidebar = mobileDrawerRef.current?.getBoundingClientRect() || triggerRect;
    const panelWidth = flyoutRef.current?.offsetWidth || 200;
    const panelHeight = flyoutRef.current?.offsetHeight || 150;
    const gap = 8;
    const top = Math.max(
      gap,
      Math.min(triggerRect.top, window.innerHeight - panelHeight - gap)
    );

    if (direction === "rtl") {
      setFlyoutPosition({
        top,
        right: Math.max(gap, window.innerWidth - sidebar.left + gap),
        left: "auto"
      });
      return;
    }

    setFlyoutPosition({
      top,
      left: Math.max(
        gap,
        Math.min(sidebar.right + gap, window.innerWidth - panelWidth - gap)
      ),
      right: "auto"
    });
  }, [direction, openFlyoutGroupKey]);

  useEffect(() => {
    if (!openFlyoutGroupKey || expanded || isMobile) return undefined;

    updateFlyoutPosition();

    const closeOnOutsidePress = (event) => {
      const trigger = groupTriggerRefs.current[openFlyoutGroupKey];
      if (trigger?.contains(event.target) || flyoutRef.current?.contains(event.target)) {
        return;
      }
      setOpenFlyoutGroupKey(null);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      const trigger = groupTriggerRefs.current[openFlyoutGroupKey];
      setOpenFlyoutGroupKey(null);
      trigger?.focus();
    };

    document.addEventListener("mousedown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", updateFlyoutPosition);
    window.addEventListener("scroll", updateFlyoutPosition, true);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", updateFlyoutPosition);
      window.removeEventListener("scroll", updateFlyoutPosition, true);
    };
  }, [expanded, isMobile, openFlyoutGroupKey, updateFlyoutPosition]);

  const logOut = () => {
    toast.success(selectedLanguage === "ar" ? "إلى اللقاء" : "Good Bye", {
      icon: "👋"
    });
    const authorization = getAdminToken();
    clearAdminToken();
    axios
      .delete(`${baseUrl}/admin/auth/logout`, {
        headers: { Authorization: authorization }
      })
      .finally(reloadPage);
  };

  if (!token) return null;

  const renderChildLinks = (group, interactive) =>
    group.children.map((child) => (
      <li key={child.key}>
        <Link
          to={child.path}
          className={`sidebar-submenu-link${
            isNavigationEntryActive(child, location.pathname) ? " is-active" : ""
          }`}
          aria-label={child.label}
          tabIndex={interactive ? undefined : -1}
          onMouseEnter={
            child.key === "registration-reviews"
              ? () => prefetchDefaultRegistrationReviewQueue("customers")
              : undefined
          }
          onFocus={
            child.key === "registration-reviews"
              ? () => prefetchDefaultRegistrationReviewQueue("customers")
              : undefined
          }
          onClick={() => {
            setOpenFlyoutGroupKey(null);
            if (isMobile) closeMobileMenu();
          }}
        >
          {child.label}
        </Link>
      </li>
    ));

  const renderLink = (entry) => {
    const active = isNavigationEntryActive(entry, location.pathname);
    return (
      <li key={entry.key}>
        <Link
          to={entry.path}
          className={`sidebar-link${active ? " is-active" : ""}`}
          aria-label={entry.label}
          title={!expanded && !isMobile ? entry.label : undefined}
          onClick={isMobile ? closeMobileMenu : undefined}
        >
          <img src={entry.icon} alt="" aria-hidden="true" />
          {(expanded || isMobile) && <span>{entry.label}</span>}
        </Link>
      </li>
    );
  };

  const renderGroup = (entry) => {
    const inlineMode = expanded || isMobile;
    const inlineOpen = inlineMode && openInlineGroupKey === entry.key;
    const flyoutOpen = !inlineMode && openFlyoutGroupKey === entry.key;
    const active = isNavigationEntryActive(entry, location.pathname);
    const submenuId = `${entry.key}-submenu`;
    const flyoutId = `${entry.key}-flyout`;

    return (
      <li key={entry.key} className={`sidebar-group${active ? " is-active" : ""}`}>
        <button
          ref={(element) => {
            if (element) groupTriggerRefs.current[entry.key] = element;
          }}
          type="button"
          className="sidebar-group-button"
          aria-expanded={inlineMode ? inlineOpen : flyoutOpen}
          aria-controls={inlineMode ? submenuId : flyoutId}
          aria-label={entry.label}
          title={!expanded && !isMobile ? entry.label : undefined}
          onClick={() => {
            if (inlineMode) {
              setOpenInlineGroupKey((current) =>
                current === entry.key ? null : entry.key
              );
            } else {
              setOpenFlyoutGroupKey((current) =>
                current === entry.key ? null : entry.key
              );
            }
          }}
        >
          <img src={entry.icon} alt="" aria-hidden="true" />
          {inlineMode && <span>{entry.label}</span>}
          {inlineMode && (
            <i
              className={`fa-solid fa-chevron-${inlineOpen ? "up" : "down"}`}
              aria-hidden="true"
            />
          )}
        </button>
        {inlineOpen && (
          <ul id={submenuId} className="sidebar-submenu">
            {renderChildLinks(entry, true)}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      <TopItems
        showLanguageToggle={showLanguageToggle}
        language={selectedLanguage}
        onToggleLanguage={onToggleLanguage}
        showMenuToggle={isMobile}
        menuOpen={mobileMenuOpen}
        onToggleMenu={mobileMenuOpen ? closeMobileMenu : openMobileMenu}
        menuToggleRef={mobileToggleRef}
        direction={direction}
      />
      <nav
        id={isMobile ? "admin-mobile-navigation" : undefined}
        ref={mobileDrawerRef}
        className={`sidebar sidebar-modern${expanded ? " is-expanded" : " is-collapsed"}${
          isMobile ? " is-mobile-drawer" : ""
        }${mobileMenuOpen ? " is-mobile-open" : ""}`}
        aria-label={labels.adminNavigation}
        aria-hidden={isMobile ? !mobileMenuOpen : undefined}
        aria-modal={isMobile ? "true" : undefined}
        role={isMobile ? "dialog" : undefined}
        dir={direction}
      >
        {!isMobile && (
          <button
            type="button"
            className="sidebar-collapse-button"
            onClick={onToggleExpanded}
            aria-label={expanded ? labels.collapse : labels.expand}
          >
            <i
              className={`fa-solid fa-chevron-${expanded ? "left" : "right"}`}
              aria-hidden="true"
            />
          </button>
        )}
        <ul className="sidebar-navigation">
          {navigation.map((entry) =>
            entry.type === "group" ? renderGroup(entry) : renderLink(entry)
          )}
        </ul>
        <button
          type="button"
          className="sidebar-logout"
          onClick={logOut}
          aria-label={labels.logout}
        >
          <img src={Logout} alt="" aria-hidden="true" />
          {(expanded || isMobile) && <span>{labels.logout}</span>}
        </button>
      </nav>
      {isMobile && (
        <div
          className={`admin-mobile-navigation-backdrop${mobileMenuOpen ? " is-open" : ""}`}
          data-testid="admin-mobile-navigation-backdrop"
          aria-hidden="true"
          onClick={closeMobileMenu}
        />
      )}
      <CollapsedNavigationFlyout
        id={activeFlyoutGroup ? `${activeFlyoutGroup.key}-flyout` : "navigation-group-flyout"}
        open={!isMobile && !expanded && Boolean(activeFlyoutGroup)}
        direction={direction}
        position={flyoutPosition}
        panelRef={flyoutRef}
        label={activeFlyoutGroup?.pagesLabel || labels.adminNavigation}
      >
        {activeFlyoutGroup
          ? renderChildLinks(
              activeFlyoutGroup,
              !isMobile && !expanded && Boolean(openFlyoutGroupKey)
            )
          : null}
      </CollapsedNavigationFlyout>
    </>
  );
};

export default Navbar;
